import { useCallback, useEffect, useReducer, useRef } from "react";
import { createEditorState, editorReducer, type EditorState } from "./EditorState";

export type EditorSaveResult = { ok: true; value?: string } | { ok: false; message?: string };

export type EditorController = {
  state: EditorState;
  value: string;
  savedValue: string;
  dirty: boolean;
  inputDisabled: boolean;
  saving: boolean;
  setValue: (value: string) => void;
  reset: () => void;
  replace: (value: string | null) => void;
  save: () => Promise<boolean>;
};

type UseEditorControllerOptions = {
  value: string | null;
  disabled?: boolean;
  isEqual?: (value: string, savedValue: string) => boolean;
  onSave?: (value: string) => Promise<EditorSaveResult>;
};

const DEFAULT_SAVE_ERROR = "저장하지 못했습니다.";

export function useEditorController({
  value,
  disabled = false,
  isEqual = Object.is,
  onSave,
}: UseEditorControllerOptions): EditorController {
  const [state, dispatch] = useReducer(editorReducer, value, createEditorState);
  const stateRef = useRef(state);
  const disabledRef = useRef(disabled);
  const onSaveRef = useRef(onSave);
  const externalValueRef = useRef(value);
  const activeRequestRef = useRef<number | null>(null);
  const nextRequestIdRef = useRef(1);
  const mountedRef = useRef(true);

  stateRef.current = state;
  disabledRef.current = disabled;
  onSaveRef.current = onSave;
  externalValueRef.current = value;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // reducer가 dirty draft를 보존하므로 polling 응답이 사용자 입력을 덮어쓰지 않는다. saving 중에
  // 들어온 값은 reducer가 무시한다. 아래 save가 완료되면 externalValueRef로 다시 판정한다.
  useEffect(() => {
    dispatch({ type: "sync", value });
  }, [value]);

  const setValue = useCallback((nextValue: string) => {
    dispatch({ type: "change", value: nextValue });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  const replace = useCallback((nextValue: string | null) => {
    activeRequestRef.current = null;
    externalValueRef.current = nextValue;
    dispatch({ type: "load", value: nextValue });
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const current = stateRef.current;
    const saveValue = onSaveRef.current;
    if (
      saveValue === undefined ||
      disabledRef.current ||
      current.status === "loading" ||
      current.status === "saving" ||
      activeRequestRef.current !== null
    ) {
      return false;
    }

    const requestId = nextRequestIdRef.current;
    nextRequestIdRef.current += 1;
    activeRequestRef.current = requestId;
    dispatch({ type: "saveStarted", requestId });

    let result: EditorSaveResult;
    try {
      result = await saveValue(current.value);
    } catch {
      result = { ok: false, message: DEFAULT_SAVE_ERROR };
    }

    if (activeRequestRef.current !== requestId) return false;
    activeRequestRef.current = null;
    if (!mountedRef.current) return result.ok;

    if (result.ok) {
      const externalValue = externalValueRef.current;
      const savedValue =
        result.value ??
        (externalValue !== null && externalValue !== current.savedValue
          ? externalValue
          : current.value);
      dispatch({ type: "saveSucceeded", requestId, value: savedValue });
      return true;
    }

    dispatch({
      type: "saveFailed",
      requestId,
      message: result.message ?? DEFAULT_SAVE_ERROR,
    });
    return false;
  }, []);

  return {
    state,
    value: state.value,
    savedValue: state.savedValue,
    dirty: !isEqual(state.value, state.savedValue),
    inputDisabled: disabled || state.status === "loading" || state.status === "saving",
    saving: state.status === "saving",
    setValue,
    reset,
    replace,
    save,
  };
}
